import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { isFirefox } from 'react-device-detect';
import styled, { css } from 'styled-components';
import { useHasSupportedVideoCodec } from '../../hooks/useHasSupportedVideoCodec';
import { BroadcastService } from '../../services/broadcast/BroadcastService';
import { MediaService } from '../../services/media/MediaService';
import { useTimedFlag } from '../../utils/time';
import { BroadcasterCanvas } from '../BroadcasterCanvas';
import { Button } from '../Button';
import { FullscreenButton } from '../FullscreenButton';
import { BroadcasterViewHud } from '../huds/BroadcasterViewHud';
import { LoadingIndicator } from '../LoadingIndicator';
import { CodecNotSupported, FirefoxNotSupported } from '../NotSupportedMessage';
import { SidebarSlot } from '../SidebarSlot';
import { Stage, StageContainer } from '../Stage';
import { SplitShell } from '../shells/SplitShell';
import { StackShell } from '../shells/StackShell';
import { VideoDisplay } from '../VideoDisplay';
import { BlockedPermissions } from '../views/View';
import { BroadcasterFooter } from './BroadcasterFooter';
import { BroadcasterHeader } from './BroadcasterHeader';
export function BroadcasterView() {
    const [showSidebar, setShowSidebar] = useState(true);
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const stageContainerRef = useRef(null);
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const handleFullscreenChange = (fullscreen) => {
        setIsFullscreen(fullscreen);
    };
    // Service-based UI
    // !! Notice the naming convention for the state variables to reduce the cognitive load
    // when reading the code. There is no need to remember where each state variable is coming from.
    // !!
    const broadcastService = BroadcastService.getNotificationInterface();
    const broadcastOutputServiceRunning = BroadcastService.getOutput().useServiceRunning();
    const broadcastOutputBroadcasting = BroadcastService.getOutput().useBroadcasting();
    // Media service and its reactive state
    const mediaService = MediaService.getNotificationInterface();
    const mediaOutputMicrophonePermissioned = MediaService.getOutput().useMicrophonePermissioned();
    const mediaOutputCameraPermissioned = MediaService.getOutput().useCameraPermissioned();
    const mediaOutputMediaStreamData = MediaService.getOutput().useMediaStreamData();
    // Event handlers
    const onRequestMediaPermissionsButtonClicked = () => {
        mediaService.notifyMediaPermissionsRequested({
            audio: true,
            video: true,
        });
    };
    useEffect(() => {
        mediaService.notifyStartCaptureRequested();
        return () => {
            mediaService.notifyStopCaptureRequested();
        };
    }, [mediaService]);
    if (isFirefox) {
        return _jsx(FirefoxNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !broadcastOutputServiceRunning }), _jsx(BroadcasterViewRoot, { "$isFullscreen": isFullscreen, before: _jsx(BroadcasterHeader, {}), after: _jsx(BroadcasterFooter, { broadcastServiceIsRunning: broadcastOutputServiceRunning, isBroadcasting: broadcastOutputBroadcasting, onGoLiveClick: () => {
                        broadcastService.notifyStartBroadcast();
                    }, onEndBroadcastClick: () => {
                        broadcastService.notifyStopBroadcast();
                    }, onSidebarClick: () => {
                        setShowSidebar((c) => !c);
                    }, showSidebar: showSidebar, broadcastContext: "broadcaster", onVideoQualityChanged: (quality) => {
                        broadcastService.notifyVideoQualityChanged(quality);
                    } }), children: _jsx(SplitShell, { isSidebarOpen: showSidebar, sidebar: _jsx(SidebarSlot, {}), children: _jsx(StageContainer, { ref: stageContainerRef, onTouchStart: () => {
                            showHud();
                        }, onMouseDown: () => {
                            showHud();
                        }, onMouseOver: () => {
                            showHud();
                        }, children: _jsx(Stage, { children: mediaOutputMicrophonePermissioned ||
                                mediaOutputCameraPermissioned ||
                                mediaOutputMediaStreamData.isSharingScreen ? (_jsx(VideoDisplay, { component: _jsx(BroadcasterCanvas, {}), fit: isFullscreen ? 'cover' : 'contain', hud: _jsx(BroadcasterViewHud, { visible: isHudVisible, fullscreenButton: _jsx(FullscreenButton, { element: stageContainerRef.current, onFullscreenChange: handleFullscreenChange }) }) })) : (_jsx(BlockedPermissions, { children: _jsx(Button, { type: "button", kind: "secondary", size: "big", padding: "normal", onClick: onRequestMediaPermissionsButtonClicked, children: "Allow microphone and camera" }) })) }) }) }) })] }));
}
const BroadcasterViewRoot = styled(StackShell) `
    --tv-ui-VideoContainer-video-border-radius: var(--tv-ui-border-radius-1);
    height: 100vh;

    ${({ $isFullscreen }) => $isFullscreen &&
    css `
            --tv-ui-VideoContainer-video-border-radius: 0;
        `}
`;
